<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();

const showDropdown = ref(false);

// Store the currently hovered label's ID
// Using JS state management instead of CSS :hover / group hover
// This will solve the flickering issue when hovering over the last label item
const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  return allLabels.value.filter(({ title }) =>
    availableContactLabels.includes(title)
  );
});

const labelMenuItems = computed(() => {
  return allLabels.value
    ?.map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      isSelected: savedLabels.value.some(
        savedLabel => savedLabel.id === label.id
      ),
      action: 'contactLabel',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async ({ value }) => {
  try {
    // Get current label titles
    const currentLabels = savedLabels.value.map(label => label.title);

    // Find the label title for the ID (value)
    const selectedLabel = allLabels.value.find(label => label.id === value);
    if (!selectedLabel) return;

    let updatedLabels;

    // If label is already selected, remove it (toggle behavior)
    if (currentLabels.includes(selectedLabel.title)) {
      updatedLabels = currentLabels.filter(
        labelTitle => labelTitle !== selectedLabel.title
      );
    } else {
      // Add the new label
      updatedLabels = [...currentLabels, selectedLabel.title];
    }

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });

    showDropdown.value = false;
  } catch (error) {
    // error
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);
onMounted(() => {
  if (route.params.contactId) {
    fetchLabels(route.params.contactId);
  }
});

const closeDropdown = () => { showDropdown.value = false; };

// SZ: ordem das opções do dropdown — não-selecionadas primeiro, depois selecionadas
const dropdownItems = computed(() => labelMenuItems.value || []);
</script>

<template>
  <!-- SZ Figma #1652706307 — linha de etiquetas (gap 4px, wrap) -->
  <div class="sz-cl-row" v-on-click-outside="closeDropdown">
    <!-- Botão "+ Etiquetas" (Figma #1652706275) — bg #DEDEDE, radius 999, height 24 -->
    <div class="sz-cl-add-wrap">
      <button
        type="button"
        class="sz-cl-add-btn"
        :aria-expanded="showDropdown"
        @click="showDropdown = !showDropdown"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span>Etiquetas</span>
      </button>
      <div v-if="showDropdown" class="sz-cl-dropdown">
        <button
          v-for="item in dropdownItems"
          :key="item.value"
          type="button"
          class="sz-cl-dd-item"
          :class="{ 'sz-cl-dd-item--on': item.isSelected }"
          @click="handleLabelAction(item)"
        >
          <span class="sz-cl-dd-dot" :style="{ background: item.thumbnail.color }" />
          <span class="sz-cl-dd-text">{{ item.label }}</span>
          <svg v-if="item.isSelected" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
        <div v-if="!dropdownItems.length" class="sz-cl-dd-empty">Sem etiquetas</div>
      </div>
    </div>

    <!-- Chips das etiquetas salvas (Figma #1652706273) — bg #FFFFFF, border 1px #F5F5F5, radius 999 -->
    <span
      v-for="label in savedLabels"
      :key="label.id"
      class="sz-cl-chip"
      :style="{ color: label.color }"
    >
      <span class="sz-cl-chip-dot" :style="{ background: label.color }" />
      <span class="sz-cl-chip-text">{{ label.title }}</span>
      <button
        type="button"
        class="sz-cl-chip-x"
        :aria-label="`Remover ${label.title}`"
        @click="handleRemoveLabel(label)"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </span>
  </div>
</template>

<style scoped>
.sz-cl-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 4px;
  width: 100%;
  min-height: 24px;
  font-family: 'Inter', system-ui, sans-serif;
}

/* "+ Etiquetas" — Figma #1652706275 */
.sz-cl-add-wrap { position: relative; }
.sz-cl-add-btn {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  gap: 8px;
  height: 24px;
  background: #DEDEDE;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  color: #000000;
  font-family: inherit;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  transition: background 0.12s;
}
.sz-cl-add-btn:hover { background: #D0D0D0; }
.dark .sz-cl-add-btn { background: #3D3F43; color: #FFFFFF; }
.dark .sz-cl-add-btn:hover { background: #4A4D52; }

/* Chip de etiqueta — Figma #1652706273 */
.sz-cl-chip {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  gap: 4px;
  height: 24px;
  background: #FFFFFF;
  border: 1px solid #F5F5F5;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  /* color é setado inline via :style baseado em label.color */
  box-sizing: border-box;
}
.dark .sz-cl-chip {
  background: #1D1E21;
  border-color: #2A2C30;
}
.sz-cl-chip-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-cl-chip-text {
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-cl-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #A1A1A1;
  flex-shrink: 0;
}
.sz-cl-chip-x:hover { color: #525252; }
.dark .sz-cl-chip-x { color: #9797A0; }
.dark .sz-cl-chip-x:hover { color: #E5E7EB; }

/* Dropdown do "+ Etiquetas" */
.sz-cl-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  min-width: 220px;
  max-height: 280px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.dark .sz-cl-dropdown {
  background: #1D1E21;
  border-color: #3D3F43;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
.sz-cl-dd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.1s ease;
}
.sz-cl-dd-item:hover { background: #F3F4F6; }
.sz-cl-dd-item--on { color: #2E7D32; }
.dark .sz-cl-dd-item { color: #E5E7EB; }
.dark .sz-cl-dd-item:hover { background: #242528; }
.dark .sz-cl-dd-item--on { color: #8FD18F; }
.sz-cl-dd-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-cl-dd-text { flex: 1; overflow: hidden; text-overflow: ellipsis; }
.sz-cl-dd-empty {
  padding: 10px;
  text-align: center;
  color: #9CA3AF;
  font-size: 13px;
}
</style>
